import {setDifference} from '@benchling/js-utils';
import React, {useEffect, useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {CloseType, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {ProcessFlowchartConfigVersionSpec} from 'processes/dao/ProcessSchemaDAO';
import {convertGQLFlowchartConfigToClient} from 'processes/graph-builder-processes/flowchart-viz/util/convertGQLFlowchartConfigToClient';
import {flowchartHasChainedRouters} from 'processes/graph-builder-processes/flowchart-viz/util/flowchartHasChainedRouters';
import constructDesignSpace from 'processes/graph-builder-processes/util/constructDesignSpace';
import {ProcessTaskSchemaEditorQuery} from 'processes/settings/__generated/ProcessTaskSchemaEditorQuery';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import convertProcessInteractiveFlowchartToFlowchartSpec from '../../graph-builder-processes/flowchart-viz/util/convertProcessInteractiveFlowchartToFlowchartSpec';
import areClientFlowchartConfigsDifferent from './areClientFlowchartConfigsDifferent';
import {
  doesSchemaMapperConfigDependOnSchemas,
  doesTemplateFlowchartDependOnSchemaMapperConfigs,
  doesTemplateFlowchartDependOnSchemas,
} from './detectFlowchartConfigDependency';
import {
  AvailableTaskSchema,
  ClientSchemaMapperConfig,
  isPlaceholderTaskSchema,
} from './FlowchartConfigConstants';
import getFlowchartConfigValidationResults from './getFlowchartConfigValidationResults';
import GraphBuilderFlowchartConfigTabContainer from './GraphBuilderFlowchartConfigTabContainer';

type FlowchartConfigVersionGQL = NonNullable<
  NonNullable<
    ExtractByTypename<
      ProcessTaskSchemaEditorQuery['processTaskSchema'],
      'ProcessTaskSchemaNode'
    >['flowchartConfig']
  >['currentFlowchartConfigVersion']
>;

interface GraphBuilderFlowchartConfigEditorBaseProps {
  isEditing: boolean;
  flowchartConfig: FlowchartConfigVersionGQL | null;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
  allSchemasById: SchemaById;
  onSetIsEditing: (isEditing: boolean) => void;
  onSubmit: (
    flowchartConfigSpec: ProcessFlowchartConfigVersionSpec,
    configEditorEventGroupId: string,
    didInitialFlowchartHaveChainedRouters: boolean
  ) => void;
}

export interface GraphBuilderFlowchartConfigEditorProps extends GraphBuilderFlowchartConfigEditorBaseProps {
  allTaskSchemas: Array<ProcessTaskSchemaGraphQL>;
}

function GraphBuilderFlowchartConfigEditor(props: GraphBuilderFlowchartConfigEditorProps): JSX.Element {
  const initialClientFlowchartConfig = useMemo(
    () =>
      // This component is only rendered if the user has access to all of the data. So this call is safe.
      convertGQLFlowchartConfigToClient(props.flowchartConfig, props.allTaskSchemas, props.rootTaskSchema),
    [props.flowchartConfig, props.allTaskSchemas, props.rootTaskSchema]
  );

  const [availableTaskSchemas, setAvailableTaskSchemas] = useState(
    initialClientFlowchartConfig.availableTaskSchemas
  );
  const [schemaMapperConfigs, setSchemaMapperConfigs] = useState(
    initialClientFlowchartConfig.schemaMapperConfigs
  );
  const [templateFlowchart, setTemplateFlowchart] = useState(initialClientFlowchartConfig.templateFlowchart);
  const [isFlowchartModifiableOnCreation, setIsFlowchartModifiableOnCreation] = useState(
    initialClientFlowchartConfig.isFlowchartModifiableOnCreation
  );
  const [isFlowchartModifiableAfterCreation, setIsFlowchartModifiableAfterCreation] = useState(
    initialClientFlowchartConfig.isFlowchartModifiableAfterCreation
  );

  const designSpace = constructDesignSpace(
    availableTaskSchemas.filter(
      (availableTaskSchema) => !isPlaceholderTaskSchema(availableTaskSchema)
    ) as Array<ProcessTaskSchemaGraphQL>,
    schemaMapperConfigs,
    props.rootTaskSchema
  );
  const [didInitialFlowchartHaveChainedRouters, _] = useState(
    flowchartHasChainedRouters(templateFlowchart, designSpace)
  );
  const validationResults = useMemo(() => {
    const results = getFlowchartConfigValidationResults({
      availableTaskSchemas,
      schemaMapperConfigs,
      templateFlowchart,
      isFlowchartModifiableOnCreation,
      rootTaskSchema: props.rootTaskSchema,
      didInitialFlowchartHaveChainedRouters,
      allowNoSchemas: true,
    });
    if (results != null) {
      return results;
    }

    if (
      !areClientFlowchartConfigsDifferent(initialClientFlowchartConfig, {
        availableTaskSchemas,
        schemaMapperConfigs,
        templateFlowchart,
        isFlowchartModifiableOnCreation,
        isFlowchartModifiableAfterCreation,
      })
    ) {
      return {
        tab: null,
        message: 'Flowchart config has not been changed',
        errorTypes: [],
      };
    }

    return null;
  }, [
    availableTaskSchemas,
    schemaMapperConfigs,
    templateFlowchart,
    isFlowchartModifiableOnCreation,
    props.rootTaskSchema,
    initialClientFlowchartConfig,
    isFlowchartModifiableAfterCreation,
    didInitialFlowchartHaveChainedRouters,
  ]);

  const setStateToServerFlowchartConfig = (): void => {
    setAvailableTaskSchemas(initialClientFlowchartConfig.availableTaskSchemas);
    setSchemaMapperConfigs(initialClientFlowchartConfig.schemaMapperConfigs);
    setTemplateFlowchart(initialClientFlowchartConfig.templateFlowchart);
    setIsFlowchartModifiableOnCreation(initialClientFlowchartConfig.isFlowchartModifiableOnCreation);
    setIsFlowchartModifiableAfterCreation(initialClientFlowchartConfig.isFlowchartModifiableAfterCreation);
  };

  useEffect(() => {
    setStateToServerFlowchartConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.rootTaskSchema]);

  const onSubmit = (configEditorEventGroupId: string): void => {
    props.onSetIsEditing(false);

    const mapperConfigs = schemaMapperConfigs.map(({clientId, fromTaskSchemas, toTaskSchema}) => ({
      clientId,
      fromTaskSchemaIds: fromTaskSchemas.map(({id}) => id),
      toTaskSchemaId: toTaskSchema!.id,
    }));

    let flowchartArgs;
    if (templateFlowchart != null) {
      flowchartArgs = {
        templateFlowchart: convertProcessInteractiveFlowchartToFlowchartSpec(
          templateFlowchart,
          mapperConfigs,
          props.rootTaskSchema
        ),
      };
    } else {
      flowchartArgs = {templateFlowchart: null};
    }

    props.onSubmit(
      {
        availableTaskSchemaIds: availableTaskSchemas.map(
          (taskSchema: ProcessTaskSchemaGraphQL) => taskSchema.id
        ),
        schemaMapperConfigs: schemaMapperConfigs
          .filter(({mapperConfig}) => mapperConfig != null)
          .map((schemaMapperConfig) => ({
            ...schemaMapperConfig.mapperConfig!,
            // TODO(haroon): The mapperConfig has different clientId than that of the schemaMapperConfig
            // due to shared code with the Rigid Linear Flowchart UI.
            // However, this is not necessary in the GraphBuilder UI and so should be removed.
            clientId: schemaMapperConfig.clientId,
          })),
        ...flowchartArgs,
        isFlowchartModifiableOnCreation,
        isFlowchartModifiableAfterCreation,
      },
      configEditorEventGroupId,
      didInitialFlowchartHaveChainedRouters
    );
  };

  const onChangeAvailableTaskSchemas = async (
    newAvailableTaskSchemas: Array<AvailableTaskSchema>
  ): Promise<void> => {
    const newTaskSchemaIdsSet = new Set(
      newAvailableTaskSchemas
        .filter((taskSchema) => !isPlaceholderTaskSchema(taskSchema))
        .map(({id}: ProcessTaskSchemaGraphQL) => id)
    );
    const removedTakSchemaIds = new Set(
      availableTaskSchemas
        .filter(
          (taskSchema) => !isPlaceholderTaskSchema(taskSchema) && !newTaskSchemaIdsSet.has(taskSchema.id)
        )
        .map(({id}: ProcessTaskSchemaGraphQL) => id)
    );

    const newSchemaMapperConfigs = schemaMapperConfigs.filter(
      (schemaMapperConfig) => !doesSchemaMapperConfigDependOnSchemas(schemaMapperConfig, removedTakSchemaIds)
    );
    const isTemplateFlowchartNowInvalid =
      templateFlowchart != null &&
      doesTemplateFlowchartDependOnSchemas(templateFlowchart, removedTakSchemaIds);

    const areSchemaMappersNowInvalid = newSchemaMapperConfigs.length !== schemaMapperConfigs.length;
    if (areSchemaMappersNowInvalid || isTemplateFlowchartNowInvalid) {
      let referencedItems: string;
      let message: string;
      if (areSchemaMappersNowInvalid && isTemplateFlowchartNowInvalid) {
        referencedItems = 'a transition and the template flowchart';
        message =
          'the transition will be removed and the template flowchart will need to be deleted or corrected before the flowchart config can be saved';
      } else if (areSchemaMappersNowInvalid) {
        referencedItems = 'a transition';
        message = 'the transition will be removed';
      } else {
        referencedItems = 'the template flowchart';
        message =
          'the template flowchart will need to be deleted or corrected before the flowchart config can be saved';
      }
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.WARNING_SHOWN_FLOWCHART_CONFIG_ITEM_DELETION, {
        process_client_origin: props.rootTaskSchema.clientOrigin,
      });
      const confirmDelete = await runConfirmDeleteModal({
        typeName: 'task schema',
        message: `This task schema is referenced by ${referencedItems}. If the task schema is removed, ${message}.`,
        onSubmit: () => {
          setAvailableTaskSchemas(newAvailableTaskSchemas);
          setSchemaMapperConfigs(newSchemaMapperConfigs);
          analytics.trackEvent(
            WORKFLOWS_ANALYTICS_ACTIONS.WARNING_OVERRIDDEN_FLOWCHART_CONFIG_ITEM_DELETION,
            {process_client_origin: props.rootTaskSchema.clientOrigin}
          );
        },
        onCancel: () => {
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.WARNING_CLOSED_FLOWCHART_CONFIG_ITEM_DELETION, {
            process_client_origin: props.rootTaskSchema.clientOrigin,
            close_type: CloseType.CANCEL,
          });
        },
        onExit: () => {
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.WARNING_CLOSED_FLOWCHART_CONFIG_ITEM_DELETION, {
            process_client_origin: props.rootTaskSchema.clientOrigin,
            close_type: CloseType.EXIT,
          });
        },
      });
      if (!confirmDelete) {
        return;
      }
    }

    setAvailableTaskSchemas(newAvailableTaskSchemas);
    setSchemaMapperConfigs(newSchemaMapperConfigs);
  };

  // TODO(BNCH-41880): Validate destructive edits to schema mapper configs
  const onChangeSchemaMapperConfigs = async (
    newSchemaMapperConfigs: Array<ClientSchemaMapperConfig>
  ): Promise<void> => {
    const removedSchemaMapperConfigIds = setDifference(
      new Set(schemaMapperConfigs.map(({clientId}) => clientId)),
      new Set(newSchemaMapperConfigs.map(({clientId}) => clientId))
    );

    if (
      templateFlowchart != null &&
      doesTemplateFlowchartDependOnSchemaMapperConfigs(
        templateFlowchart,
        schemaMapperConfigs,
        removedSchemaMapperConfigIds
      )
    ) {
      const confirmDelete = await runConfirmDeleteModal({
        typeName: 'transition',
        message:
          'This transition is referenced by the template flowchart. If removed, the template flowchart will need to be deleted or corrected before the flowchart config can be saved.',
      });
      if (!confirmDelete) {
        return;
      }
    }

    setSchemaMapperConfigs(newSchemaMapperConfigs);
  };

  return (
    <GraphBuilderFlowchartConfigTabContainer
      availableTaskSchemas={availableTaskSchemas}
      schemaMapperConfigs={schemaMapperConfigs}
      templateFlowchart={templateFlowchart}
      isFlowchartModifiableOnCreation={isFlowchartModifiableOnCreation}
      isFlowchartModifiableAfterCreation={isFlowchartModifiableAfterCreation}
      isEditing={props.isEditing}
      rootTaskSchema={props.rootTaskSchema}
      allSchemasById={props.allSchemasById}
      allTaskSchemas={props.allTaskSchemas}
      validationResults={validationResults}
      didInitialFlowchartHaveChainedRouters={didInitialFlowchartHaveChainedRouters}
      onEnableEditing={() => {
        props.onSetIsEditing(true);
      }}
      onSubmit={onSubmit}
      onCancel={() => {
        setStateToServerFlowchartConfig();
        props.onSetIsEditing(false);
      }}
      onChangeAvailableTaskSchemas={onChangeAvailableTaskSchemas}
      onChangeSchemaMapperConfigs={onChangeSchemaMapperConfigs}
      onChangeTemplateFlowchart={setTemplateFlowchart}
      onChangeIsFlowchartModifiableOnCreation={setIsFlowchartModifiableOnCreation}
      onChangeIsFlowchartModifiableAfterCreation={setIsFlowchartModifiableAfterCreation}
    />
  );
}

export interface GraphBuilderFlowchartConfigEditorLoadingWrapperProps
  extends GraphBuilderFlowchartConfigEditorBaseProps {
  allTaskSchemas: Array<ProcessTaskSchemaGraphQL> | null;
}

export default function GraphBuilderFlowchartConfigEditorLoadingWrapper(
  props: GraphBuilderFlowchartConfigEditorLoadingWrapperProps
): JSX.Element {
  if (props.allTaskSchemas == null) {
    return <CenteredHorizontalThrobber />;
  }

  return <GraphBuilderFlowchartConfigEditor {...props} allTaskSchemas={props.allTaskSchemas} />;
}
